import { useEffect, useRef, useState, useCallback } from 'preact/hooks';

export type ConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'closed';

export interface UseLiveSocketResult {
  state: ConnectionState;
  disconnect: () => void;
}

interface UseLiveSocketOptions<T> {
  /** Resolve the ws(s):// URL. May throw to signal "nothing to connect to" (socket closes).
   *
   *  May be ASYNC (D-137): the cloud fleet socket has to mint a single-use ticket
   *  before every attempt, including each reconnect, so the URL cannot be known
   *  synchronously. Returning a plain string still works unchanged. */
  resolveUrl: () => string | Promise<string>;
  /** Called for every incoming frame (parsed JSON). */
  onEvent: (event: T) => void;
  enabled?: boolean;
  /** Reconnect whenever any of these change (e.g. room code). */
  deps?: readonly unknown[];
}

const BACKOFF_MS = [1_000, 2_000, 5_000, 10_000, 30_000] as const;

/**
 * Shared reconnecting-WebSocket engine for iot-live-local: receives a snapshot then live
 * JSON frames, with exponential-backoff reconnect. Channel-agnostic — `useLiveRoom` wraps it
 * with its own URL resolver so the connection logic lives once. (Device liveness has its own
 * shared, ref-counted socket in `liveHealth.store.ts`.)
 */
export function useLiveSocket<T>({
  resolveUrl,
  onEvent,
  enabled = true,
  deps = [],
}: UseLiveSocketOptions<T>): UseLiveSocketResult {
  const [state, setState] = useState<ConnectionState>('connecting');
  const wsRef = useRef<WebSocket | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attemptRef = useRef(0);
  // Guards the reconnect scheduler against a unit that has torn down. `WebSocket.close()` fires
  // `onclose` ASYNCHRONOUSLY — after the effect cleanup has already run its `clearTimer()`. Without
  // this flag that late `onclose` would schedule a fresh reconnect the cleanup can no longer cancel,
  // leaking a socket that reconnects forever; navigate around a few screens and the browser's socket
  // pool exhausts ("WebSocket … Insufficient resources"). Set false on teardown → onclose is inert.
  const aliveRef = useRef(true);
  const onEventRef = useRef(onEvent);
  const resolveRef = useRef(resolveUrl);
  onEventRef.current = onEvent;
  resolveRef.current = resolveUrl;

  const clearTimer = () => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  /** Detach every handler before closing so a teardown close can't drive state or reconnect. */
  const teardownSocket = () => {
    const ws = wsRef.current;
    if (ws) {
      ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
      ws.close();
      wsRef.current = null;
    }
  };

  const connect = useCallback(() => {
    // Kept as a fire-and-forget async body rather than making `connect` itself
    // async, so the `setTimeout(connect, delay)` reconnect path is unchanged.
    void (async () => {
      let url: string;
      try {
        url = await resolveRef.current();
      } catch {
        // e.g. cloud mode with no site selected, or a ticket mint that failed —
        // nothing to connect to. The reconnect below still retries with backoff.
        if (!enabled || !aliveRef.current) {
          setState('closed');
          return;
        }
        const delay = BACKOFF_MS[Math.min(attemptRef.current, BACKOFF_MS.length - 1)] ?? 30_000;
        attemptRef.current++;
        setState('reconnecting');
        timerRef.current = setTimeout(connect, delay);
        return;
      }
      // Resolving can await (a ticket fetch), so the unit may have torn down in the
      // meantime. Opening a socket now would leak one the cleanup can no longer see.
      if (!aliveRef.current || !enabled) {
        setState('closed');
        return;
      }
      openSocket(url);
    })();
  }, [enabled]);

  /** Open the socket for an already-resolved URL, and own the reconnect policy. */
  const openSocket = (url: string) => {
    const ws = new WebSocket(url);
    wsRef.current = ws;
    setState('connecting');

    ws.onopen = () => {
      attemptRef.current = 0;
      setState('connected');
    };

    ws.onmessage = (e: MessageEvent) => {
      try {
        onEventRef.current(JSON.parse(e.data) as T);
      } catch {
        /* malformed frame */
      }
    };

    ws.onclose = () => {
      wsRef.current = null;
      // Torn down or disabled → do not resurrect the socket.
      if (!enabled || !aliveRef.current) {
        setState('closed');
        return;
      }
      const delay = BACKOFF_MS[Math.min(attemptRef.current, BACKOFF_MS.length - 1)] ?? 30_000;
      attemptRef.current++;
      setState('reconnecting');
      timerRef.current = setTimeout(connect, delay);
    };

    ws.onerror = () => {
      ws.close();
    };
  };

  const disconnect = useCallback(() => {
    clearTimer();
    teardownSocket();
    setState('closed');
  }, []);

  useEffect(() => {
    if (!enabled) {
      setState('closed');
      return;
    }
    aliveRef.current = true;
    attemptRef.current = 0;
    connect();
    return () => {
      // Order matters: mark dead FIRST so the close-triggered onclose (if any handler survives) and
      // any in-flight backoff both see a torn-down unit; then cancel the timer and drop the socket.
      aliveRef.current = false;
      clearTimer();
      teardownSocket();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, connect, ...deps]);

  return { state, disconnect };
}
